import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { 
  ArrowLeft, Save, Loader2, JapaneseYen, Building2, CreditCard, Shield, 
  Calculator, Clock, User, MapPin, Phone, Mail, Landmark, Heart, FileText, Download
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const EmployeeSalaryPage = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("salary");
  
  // 従業員情報
  const [employee, setEmployee] = useState(null);
  
  // 勤怠パターン一覧
  const [patterns, setPatterns] = useState([]);
  
  // 給与情報
  const [salaryInfo, setSalaryInfo] = useState({
    salary_type: "monthly",
    base_salary: 0,
    hourly_rate: 0,
    monthly_working_days: 20,
    daily_working_hours: 8.0,
    attendance_pattern_id: "",
    commute_type: "none",
    commute_monthly: 0,
    commute_daily: 0,
    commute_distance_km: 0,
    commute_rate_per_km: 0,
    commute_tax_exempt: 0,
    fixed_allowances: [],
    fixed_deductions: [],
    bank_code: "",
    bank_name: "",
    branch_code: "",
    branch_name: "",
    account_type: "ordinary",
    account_number: "",
    account_holder: "",
    account_holder_kana: "",
    use_electronic_transfer: false
  });
  
  // 社会保険情報
  const [socialInsurance, setSocialInsurance] = useState({
    health_insurance_enrolled: false,
    health_insurance_number: "",
    health_insurance_grade: null,
    health_insurance_monthly: 0,
    health_insurance_start: "",
    nursing_insurance_enrolled: false,
    pension_enrolled: false,
    pension_number: "",
    pension_grade: null,
    pension_monthly: 0,
    pension_start: "",
    employment_insurance_enrolled: false,
    employment_insurance_number: "",
    employment_insurance_type: "general",
    employment_insurance_start: "",
    workers_comp_enrolled: true,
    resident_tax_type: "special",
    resident_tax_monthly: 0,
    resident_tax_yearly: 0,
    dependents_count: 0,
    has_spouse: false,
    spouse_deduction: false,
    spouse_special_deduction: false
  });

  useEffect(() => {
    fetchData();
  }, [employeeId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 従業員情報を取得
      const empRes = await axios.get(`${API}/employees`);
      const emp = empRes.data.find(e => e.id === employeeId);
      if (!emp) {
        toast.error("従業員が見つかりません");
        navigate("/admin/employees");
        return;
      }
      setEmployee(emp);
      
      // 勤怠パターン一覧を取得
      const patternsRes = await axios.get(`${API}/attendance-settings/patterns`);
      setPatterns(patternsRes.data);
      
      // 給与情報を取得
      const salaryRes = await axios.get(`${API}/employees/${employeeId}/salary-info`);
      setSalaryInfo({...salaryInfo, ...salaryRes.data});
      
      // 社会保険情報を取得
      const insuranceRes = await axios.get(`${API}/employees/${employeeId}/social-insurance`);
      setSocialInsurance({...socialInsurance, ...insuranceRes.data});
      
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const saveSalaryInfo = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/employees/${employeeId}/salary-info`, salaryInfo);
      toast.success("給与情報を保存しました");
    } catch (error) {
      toast.error("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const saveSocialInsurance = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/employees/${employeeId}/social-insurance`, socialInsurance);
      toast.success("社会保険情報を保存しました");
    } catch (error) {
      toast.error("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  // PDF届出書ダウンロード
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  const downloadPdf = async (documentType, filename) => {
    setDownloadingPdf(documentType);
    try {
      const response = await axios.get(`${API}/documents/${documentType}?employee_id=${employeeId}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`${filename} をダウンロードしました`);
    } catch (error) {
      console.error("PDF download error:", error);
      toast.error("PDFのダウンロードに失敗しました");
    } finally {
      setDownloadingPdf(null);
    }
  };

  // 時給計算
  const calculateHourlyRate = () => {
    if (salaryInfo.salary_type === "monthly" && salaryInfo.base_salary > 0) {
      const monthly = salaryInfo.base_salary;
      const days = salaryInfo.monthly_working_days || 20;
      const hours = salaryInfo.daily_working_hours || 8;
      const hourly = Math.round(monthly / (days * hours));
      setSalaryInfo({...salaryInfo, hourly_rate: hourly});
      toast.success(`時給を計算しました: ¥${hourly.toLocaleString()}`);
    }
  };

  // 日給計算
  const calculateDailyRate = () => {
    if (salaryInfo.hourly_rate > 0 && salaryInfo.daily_working_hours > 0) {
      const daily = Math.round(salaryInfo.hourly_rate * salaryInfo.daily_working_hours);
      toast.success(`日給: ¥${daily.toLocaleString()}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="p-6 text-center text-slate-400">
        従業員が見つかりません
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/admin/employees")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            戻る
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <User className="w-6 h-6 text-emerald-500" />
              {employee.name}
            </h1>
            <p className="text-slate-400 text-sm">
              {employee.employee_code} | 給与・社会保険情報
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-800 border border-slate-700 flex flex-wrap h-auto p-1">
          <TabsTrigger value="salary" className="data-[state=active]:bg-emerald-600 gap-2">
            <JapaneseYen className="w-4 h-4" />
            給与基礎
          </TabsTrigger>
          <TabsTrigger value="bank" className="data-[state=active]:bg-emerald-600 gap-2">
            <Landmark className="w-4 h-4" />
            振込先銀行
          </TabsTrigger>
          <TabsTrigger value="commute" className="data-[state=active]:bg-emerald-600 gap-2">
            <MapPin className="w-4 h-4" />
            通勤手当
          </TabsTrigger>
          <TabsTrigger value="insurance" className="data-[state=active]:bg-emerald-600 gap-2">
            <Shield className="w-4 h-4" />
            社保・労保
          </TabsTrigger>
          <TabsTrigger value="tax" className="data-[state=active]:bg-emerald-600 gap-2">
            <Building2 className="w-4 h-4" />
            所得・住民税
          </TabsTrigger>
        </TabsList>

        {/* 給与基礎タブ */}
        <TabsContent value="salary" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">給与基礎情報</CardTitle>
              <CardDescription>給与体系、基本給、勤怠パターンを設定します</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 給与体系 */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>給与体系</Label>
                  <Select
                    value={salaryInfo.salary_type}
                    onValueChange={(v) => setSalaryInfo({...salaryInfo, salary_type: v})}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">月給</SelectItem>
                      <SelectItem value="daily">日給</SelectItem>
                      <SelectItem value="daily_monthly">日給月給</SelectItem>
                      <SelectItem value="hourly">時給</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>基本給（月額）</Label>
                  <div className="relative">
                    <JapaneseYen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      value={salaryInfo.base_salary}
                      onChange={(e) => setSalaryInfo({...salaryInfo, base_salary: parseInt(e.target.value) || 0})}
                      className="bg-slate-900 border-slate-700 pl-9"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>時給</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <JapaneseYen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="number"
                        value={salaryInfo.hourly_rate}
                        onChange={(e) => setSalaryInfo({...salaryInfo, hourly_rate: parseInt(e.target.value) || 0})}
                        className="bg-slate-900 border-slate-700 pl-9"
                      />
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={calculateHourlyRate}
                      className="border-slate-600"
                    >
                      <Calculator className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">月給から自動計算</p>
                </div>
                <div className="space-y-2">
                  <Label>勤怠パターン</Label>
                  <Select
                    value={salaryInfo.attendance_pattern_id || ""}
                    onValueChange={(v) => setSalaryInfo({...salaryInfo, attendance_pattern_id: v})}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700">
                      <SelectValue placeholder="選択" />
                    </SelectTrigger>
                    <SelectContent>
                      {patterns.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.code}: {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* 所定労働 */}
              <div className="pt-4 border-t border-slate-700">
                <h4 className="font-medium text-white mb-4">所定労働設定</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>月間所定労働日数</Label>
                    <Input
                      type="number"
                      value={salaryInfo.monthly_working_days}
                      onChange={(e) => setSalaryInfo({...salaryInfo, monthly_working_days: parseInt(e.target.value) || 20})}
                      className="bg-slate-900 border-slate-700"
                    />
                    <p className="text-xs text-slate-500">通常: 20日</p>
                  </div>
                  <div className="space-y-2">
                    <Label>日間所定労働時間</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={salaryInfo.daily_working_hours}
                      onChange={(e) => setSalaryInfo({...salaryInfo, daily_working_hours: parseFloat(e.target.value) || 8})}
                      className="bg-slate-900 border-slate-700"
                    />
                    <p className="text-xs text-slate-500">通常: 8時間</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={saveSalaryInfo}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  保存
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 振込先銀行タブ */}
        <TabsContent value="bank" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">振込先銀行情報</CardTitle>
              <CardDescription>給与振込先の銀行口座を設定します</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>銀行コード</Label>
                  <Input
                    value={salaryInfo.bank_code}
                    onChange={(e) => setSalaryInfo({...salaryInfo, bank_code: e.target.value})}
                    placeholder="4桁"
                    maxLength={4}
                    className="bg-slate-900 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>銀行名</Label>
                  <Input
                    value={salaryInfo.bank_name}
                    onChange={(e) => setSalaryInfo({...salaryInfo, bank_name: e.target.value})}
                    placeholder="○○銀行"
                    className="bg-slate-900 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>支店コード</Label>
                  <Input
                    value={salaryInfo.branch_code}
                    onChange={(e) => setSalaryInfo({...salaryInfo, branch_code: e.target.value})}
                    placeholder="3桁"
                    maxLength={3}
                    className="bg-slate-900 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>支店名</Label>
                  <Input
                    value={salaryInfo.branch_name}
                    onChange={(e) => setSalaryInfo({...salaryInfo, branch_name: e.target.value})}
                    placeholder="○○支店"
                    className="bg-slate-900 border-slate-700"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>預金種目</Label>
                  <Select
                    value={salaryInfo.account_type}
                    onValueChange={(v) => setSalaryInfo({...salaryInfo, account_type: v})}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ordinary">普通</SelectItem>
                      <SelectItem value="current">当座</SelectItem>
                      <SelectItem value="savings">貯蓄</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>口座番号</Label>
                  <Input
                    value={salaryInfo.account_number}
                    onChange={(e) => setSalaryInfo({...salaryInfo, account_number: e.target.value})}
                    placeholder="7桁"
                    maxLength={7}
                    className="bg-slate-900 border-slate-700"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={salaryInfo.use_electronic_transfer}
                    onCheckedChange={(checked) => setSalaryInfo({...salaryInfo, use_electronic_transfer: checked})}
                  />
                  <Label>電信扱い</Label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>口座名義（カナ）</Label>
                  <Input
                    value={salaryInfo.account_holder_kana}
                    onChange={(e) => setSalaryInfo({...salaryInfo, account_holder_kana: e.target.value})}
                    placeholder="ヤマダ タロウ"
                    className="bg-slate-900 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>口座名義</Label>
                  <Input
                    value={salaryInfo.account_holder}
                    onChange={(e) => setSalaryInfo({...salaryInfo, account_holder: e.target.value})}
                    placeholder="山田 太郎"
                    className="bg-slate-900 border-slate-700"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={saveSalaryInfo}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  保存
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 通勤手当タブ */}
        <TabsContent value="commute" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">通勤手当設定</CardTitle>
              <CardDescription>通勤区分と手当額を設定します</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>通勤区分</Label>
                  <Select
                    value={salaryInfo.commute_type}
                    onValueChange={(v) => setSalaryInfo({...salaryInfo, commute_type: v})}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">なし</SelectItem>
                      <SelectItem value="train">電車・バス</SelectItem>
                      <SelectItem value="car">自家用車</SelectItem>
                      <SelectItem value="bicycle">自転車・徒歩</SelectItem>
                      <SelectItem value="other">その他</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {salaryInfo.commute_type === "train" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>1ヶ月定期代</Label>
                    <div className="relative">
                      <JapaneseYen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="number"
                        value={salaryInfo.commute_monthly}
                        onChange={(e) => setSalaryInfo({...salaryInfo, commute_monthly: parseInt(e.target.value) || 0})}
                        className="bg-slate-900 border-slate-700 pl-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>片道交通費</Label>
                    <div className="relative">
                      <JapaneseYen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="number"
                        value={salaryInfo.commute_daily}
                        onChange={(e) => setSalaryInfo({...salaryInfo, commute_daily: parseInt(e.target.value) || 0})}
                        className="bg-slate-900 border-slate-700 pl-9"
                      />
                    </div>
                  </div>
                </div>
              )}

              {salaryInfo.commute_type === "car" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>通勤距離（片道km）</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={salaryInfo.commute_distance_km}
                      onChange={(e) => setSalaryInfo({...salaryInfo, commute_distance_km: parseFloat(e.target.value) || 0})}
                      className="bg-slate-900 border-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>キロ単価</Label>
                    <div className="relative">
                      <JapaneseYen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="number"
                        value={salaryInfo.commute_rate_per_km}
                        onChange={(e) => setSalaryInfo({...salaryInfo, commute_rate_per_km: parseInt(e.target.value) || 0})}
                        className="bg-slate-900 border-slate-700 pl-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>月額通勤手当</Label>
                    <div className="relative">
                      <JapaneseYen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="number"
                        value={salaryInfo.commute_monthly}
                        onChange={(e) => setSalaryInfo({...salaryInfo, commute_monthly: parseInt(e.target.value) || 0})}
                        className="bg-slate-900 border-slate-700 pl-9"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>非課税限度額</Label>
                <div className="relative w-64">
                  <JapaneseYen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    value={salaryInfo.commute_tax_exempt}
                    onChange={(e) => setSalaryInfo({...salaryInfo, commute_tax_exempt: parseInt(e.target.value) || 0})}
                    className="bg-slate-900 border-slate-700 pl-9"
                  />
                </div>
                <p className="text-xs text-slate-500">電車・バス: 月150,000円まで非課税</p>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={saveSalaryInfo}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  保存
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 社保・労保タブ */}
        <TabsContent value="insurance" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">社会保険・労働保険</CardTitle>
              <CardDescription>健康保険、厚生年金、雇用保険の情報を管理します</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 健康保険 */}
              <div className="p-4 bg-slate-900 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <Heart className="w-4 h-4 text-red-400" />
                    健康保険
                  </h4>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={socialInsurance.health_insurance_enrolled}
                      onCheckedChange={(checked) => setSocialInsurance({...socialInsurance, health_insurance_enrolled: checked})}
                    />
                    <Label>加入</Label>
                  </div>
                </div>
                {socialInsurance.health_insurance_enrolled && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">被保険者番号</Label>
                      <Input
                        value={socialInsurance.health_insurance_number || ""}
                        onChange={(e) => setSocialInsurance({...socialInsurance, health_insurance_number: e.target.value})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">等級</Label>
                      <Input
                        type="number"
                        value={socialInsurance.health_insurance_grade || ""}
                        onChange={(e) => setSocialInsurance({...socialInsurance, health_insurance_grade: parseInt(e.target.value) || null})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">標準報酬月額</Label>
                      <div className="relative">
                        <JapaneseYen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="number"
                          value={socialInsurance.health_insurance_monthly}
                          onChange={(e) => setSocialInsurance({...socialInsurance, health_insurance_monthly: parseInt(e.target.value) || 0})}
                          className="bg-slate-800 border-slate-700 pl-9"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">資格取得日</Label>
                      <Input
                        type="date"
                        value={socialInsurance.health_insurance_start || ""}
                        onChange={(e) => setSocialInsurance({...socialInsurance, health_insurance_start: e.target.value})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
                  <Switch
                    checked={socialInsurance.nursing_insurance_enrolled}
                    onCheckedChange={(checked) => setSocialInsurance({...socialInsurance, nursing_insurance_enrolled: checked})}
                  />
                  <Label>介護保険対象（40歳以上）</Label>
                </div>
              </div>

              {/* 厚生年金 */}
              <div className="p-4 bg-slate-900 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-400" />
                    厚生年金
                  </h4>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={socialInsurance.pension_enrolled}
                      onCheckedChange={(checked) => setSocialInsurance({...socialInsurance, pension_enrolled: checked})}
                    />
                    <Label>加入</Label>
                  </div>
                </div>
                {socialInsurance.pension_enrolled && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">基礎年金番号</Label>
                      <Input
                        value={socialInsurance.pension_number || ""}
                        onChange={(e) => setSocialInsurance({...socialInsurance, pension_number: e.target.value})}
                        placeholder="xxxx-xxxxxx"
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">等級</Label>
                      <Input
                        type="number"
                        value={socialInsurance.pension_grade || ""}
                        onChange={(e) => setSocialInsurance({...socialInsurance, pension_grade: parseInt(e.target.value) || null})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">標準報酬月額</Label>
                      <div className="relative">
                        <JapaneseYen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="number"
                          value={socialInsurance.pension_monthly}
                          onChange={(e) => setSocialInsurance({...socialInsurance, pension_monthly: parseInt(e.target.value) || 0})}
                          className="bg-slate-800 border-slate-700 pl-9"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">資格取得日</Label>
                      <Input
                        type="date"
                        value={socialInsurance.pension_start || ""}
                        onChange={(e) => setSocialInsurance({...socialInsurance, pension_start: e.target.value})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* 雇用保険 */}
              <div className="p-4 bg-slate-900 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-green-400" />
                    雇用保険
                  </h4>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={socialInsurance.employment_insurance_enrolled}
                      onCheckedChange={(checked) => setSocialInsurance({...socialInsurance, employment_insurance_enrolled: checked})}
                    />
                    <Label>加入</Label>
                  </div>
                </div>
                {socialInsurance.employment_insurance_enrolled && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">被保険者番号</Label>
                      <Input
                        value={socialInsurance.employment_insurance_number || ""}
                        onChange={(e) => setSocialInsurance({...socialInsurance, employment_insurance_number: e.target.value})}
                        placeholder="xxxx-xxxxxx-x"
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">被保険者区分</Label>
                      <Select
                        value={socialInsurance.employment_insurance_type}
                        onValueChange={(v) => setSocialInsurance({...socialInsurance, employment_insurance_type: v})}
                      >
                        <SelectTrigger className="bg-slate-800 border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">一般</SelectItem>
                          <SelectItem value="construction">建設</SelectItem>
                          <SelectItem value="agriculture">農林水産</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">資格取得日</Label>
                      <Input
                        type="date"
                        value={socialInsurance.employment_insurance_start || ""}
                        onChange={(e) => setSocialInsurance({...socialInsurance, employment_insurance_start: e.target.value})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* 労災保険 */}
              <div className="p-4 bg-slate-900 rounded-lg">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orange-400" />
                    労災保険
                  </h4>
                  <Badge className="bg-emerald-600">全員加入</Badge>
                </div>
                <p className="text-sm text-slate-400 mt-2">労災保険は会社が全額負担し、全従業員が対象です</p>
              </div>

              {/* 届出書出力セクション */}
              <div className="p-4 bg-slate-900 rounded-lg space-y-4">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-400" />
                  届出書出力（PDF）
                </h4>
                <p className="text-sm text-slate-400">社会保険・雇用保険の届出書類をPDFで出力します</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* 健康保険・厚生年金 資格取得届 */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-slate-600 hover:bg-slate-700 justify-start gap-2 h-auto py-3 px-4"
                    onClick={() => downloadPdf('shikaku-shutoku', `資格取得届_${employee?.name || employeeId.slice(0,8)}.pdf`)}
                    disabled={downloadingPdf !== null}
                    data-testid="btn-shikaku-shutoku"
                  >
                    {downloadingPdf === 'shikaku-shutoku' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <div className="text-left">
                      <div className="text-sm font-medium">資格取得届</div>
                      <div className="text-xs text-slate-400">健保・厚生年金</div>
                    </div>
                  </Button>

                  {/* 健康保険・厚生年金 資格喪失届 */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-slate-600 hover:bg-slate-700 justify-start gap-2 h-auto py-3 px-4"
                    onClick={() => downloadPdf('shikaku-soshitsu', `資格喪失届_${employee?.name || employeeId.slice(0,8)}.pdf`)}
                    disabled={downloadingPdf !== null}
                    data-testid="btn-shikaku-soshitsu"
                  >
                    {downloadingPdf === 'shikaku-soshitsu' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <div className="text-left">
                      <div className="text-sm font-medium">資格喪失届</div>
                      <div className="text-xs text-slate-400">健保・厚生年金</div>
                    </div>
                  </Button>

                  {/* 報酬月額変更届 */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-slate-600 hover:bg-slate-700 justify-start gap-2 h-auto py-3 px-4"
                    onClick={() => downloadPdf('getsugaku-henko', `月額変更届_${employee?.name || employeeId.slice(0,8)}.pdf`)}
                    disabled={downloadingPdf !== null}
                    data-testid="btn-getsugaku-henko"
                  >
                    {downloadingPdf === 'getsugaku-henko' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <div className="text-left">
                      <div className="text-sm font-medium">月額変更届</div>
                      <div className="text-xs text-slate-400">健保・厚生年金</div>
                    </div>
                  </Button>

                  {/* 雇用保険 資格取得届 */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-slate-600 hover:bg-slate-700 justify-start gap-2 h-auto py-3 px-4"
                    onClick={() => downloadPdf('koyou-hoken-shutoku', `雇用保険取得届_${employee?.name || employeeId.slice(0,8)}.pdf`)}
                    disabled={downloadingPdf !== null}
                    data-testid="btn-koyou-hoken-shutoku"
                  >
                    {downloadingPdf === 'koyou-hoken-shutoku' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <div className="text-left">
                      <div className="text-sm font-medium">資格取得届</div>
                      <div className="text-xs text-slate-400">雇用保険</div>
                    </div>
                  </Button>

                  {/* 離職証明書 */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-slate-600 hover:bg-slate-700 justify-start gap-2 h-auto py-3 px-4"
                    onClick={() => downloadPdf('rishoku-shomeisho', `離職証明書_${employee?.name || employeeId.slice(0,8)}.pdf`)}
                    disabled={downloadingPdf !== null}
                    data-testid="btn-rishoku-shomeisho"
                  >
                    {downloadingPdf === 'rishoku-shomeisho' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <div className="text-left">
                      <div className="text-sm font-medium">離職証明書</div>
                      <div className="text-xs text-slate-400">雇用保険</div>
                    </div>
                  </Button>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={saveSocialInsurance}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  保存
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 所得・住民税タブ */}
        <TabsContent value="tax" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">所得・住民税情報</CardTitle>
              <CardDescription>住民税の徴収方法と扶養情報を管理します</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 住民税 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>徴収方法</Label>
                  <Select
                    value={socialInsurance.resident_tax_type}
                    onValueChange={(v) => setSocialInsurance({...socialInsurance, resident_tax_type: v})}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="special">特別徴収（給与天引き）</SelectItem>
                      <SelectItem value="ordinary">普通徴収（自己納付）</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>月額住民税</Label>
                  <div className="relative">
                    <JapaneseYen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      value={socialInsurance.resident_tax_monthly}
                      onChange={(e) => setSocialInsurance({...socialInsurance, resident_tax_monthly: parseInt(e.target.value) || 0})}
                      className="bg-slate-900 border-slate-700 pl-9"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>年額住民税</Label>
                  <div className="relative">
                    <JapaneseYen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      value={socialInsurance.resident_tax_yearly}
                      onChange={(e) => setSocialInsurance({...socialInsurance, resident_tax_yearly: parseInt(e.target.value) || 0})}
                      className="bg-slate-900 border-slate-700 pl-9"
                    />
                  </div>
                </div>
              </div>

              {/* 扶養情報 */}
              <div className="pt-4 border-t border-slate-700">
                <h4 className="font-medium text-white mb-4">扶養控除情報</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>扶養人数</Label>
                    <Input
                      type="number"
                      value={socialInsurance.dependents_count}
                      onChange={(e) => setSocialInsurance({...socialInsurance, dependents_count: parseInt(e.target.value) || 0})}
                      className="bg-slate-900 border-slate-700"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch
                      checked={socialInsurance.has_spouse}
                      onCheckedChange={(checked) => setSocialInsurance({...socialInsurance, has_spouse: checked})}
                    />
                    <Label>配偶者あり</Label>
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch
                      checked={socialInsurance.spouse_deduction}
                      onCheckedChange={(checked) => setSocialInsurance({...socialInsurance, spouse_deduction: checked})}
                      disabled={!socialInsurance.has_spouse}
                    />
                    <Label>配偶者控除</Label>
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch
                      checked={socialInsurance.spouse_special_deduction}
                      onCheckedChange={(checked) => setSocialInsurance({...socialInsurance, spouse_special_deduction: checked})}
                      disabled={!socialInsurance.has_spouse}
                    />
                    <Label>配偶者特別控除</Label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={saveSocialInsurance}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  保存
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployeeSalaryPage;
